// 2026-05-26 - FormuLearning - Step 1 검색 섹션
// 2026-11-03 수정 - 이슈 #114: viewer 는 검색 입력·Search 비활성(툴팁 «조회 전용 계정»).
// 파일 위치: frontend/src/features/step1/SearchSection.tsx
// 2026-08-26 (이슈 #61, M84-40): 입력 타입 드롭다운 신설. 종전에는 한 입력창이 이름·SMILES·CAS 를
//   모두 받고 시스템이 종류를 추정했는데, 그 추정이 오판해 SMILES·CAS 검색이 어긋났다(안건3).
//   **검색 버튼은 1개를 유지한다** — 안건3의 명시 요구다.
// 2026-09-17 (이슈 #79, M915-03): **검색 중 잠금 + Break.** 검색 중에는 검색어·입력 타입·제출을 잠그고,
//   끊는 길은 Break 하나만 둔다. Enter 제출은 기본 버튼이 disabled 면 브라우저가 막지만(260917 실측),
//   버튼 구성이 바뀌어도 잠금이 유지되게 `handleSubmit` 에서도 확인한다.
//   Break 는 검색 중에만 나타나므로 «검색 버튼 1개»는 그대로다.

import { useEffect, useState } from 'react'
import type { SearchInputType } from '../../api/step1'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  searchInput: string | null
  resolvedEn?: string | null
  resolvedKo?: string | null
  onSearch: (query: string, inputType: SearchInputType) => Promise<void>
  loading: boolean
  /** 중단 요청을 보내고 서버 응답을 기다리는 중(#79) */
  breaking?: boolean
  onBreak?: () => void
}

const INPUT_TYPES: { value: SearchInputType; label: string; placeholder: string }[] = [
  { value: 'name', label: 'Chemical name', placeholder: '성분명 또는 제품명 (예: Amiodarone hydrochloride)' },
  { value: 'smiles', label: 'SMILES', placeholder: 'SMILES 문자열 (예: CC(=O)OC1=CC=CC=C1C(=O)O)' },
  { value: 'cas', label: 'CAS number', placeholder: 'CAS 등록번호 (예: 19774-82-4)' },
]

export default function SearchSection({ searchInput, resolvedEn, resolvedKo, onSearch, loading, breaking, onBreak }: Props) {
  const canWrite = useCanWrite() // viewer(#114) — 검색 입력·실행 비활성
  const [query, setQuery] = useState(searchInput || '')
  const [inputType, setInputType] = useState<SearchInputType>('name')
  const active = INPUT_TYPES.find((t) => t.value === inputType) ?? INPUT_TYPES[0]

  // 2026-06-26 - 프로젝트 전환/검색어 변경 시 입력 박스를 현재 검색어로 동기화 (#24-1)
  // 입력 박스는 로컬 state라 prop만 바뀌면 갱신되지 않아 이전 프로젝트 검색어가 남던 버그.
  useEffect(() => {
    setQuery(searchInput || '')
  }, [searchInput])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (loading || !canWrite) return // Enter 경로도 버튼과 같이 잠근다(#79·#114)
    if (query.trim()) onSearch(query.trim(), inputType)
  }

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <select
          value={inputType}
          onChange={(e) => setInputType(e.target.value as SearchInputType)}
          disabled={loading || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          aria-label="검색 입력 타입"
          className="rounded-md border bg-bg-input px-2 py-2 text-xs text-text-body outline-none disabled:opacity-50"
          style={{ borderColor: 'var(--border-soft)' }}
        >
          {INPUT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          placeholder={active.placeholder}
          className="flex-1 rounded-md border bg-bg-input px-3 py-2 text-sm text-text-body outline-none disabled:opacity-50"
          style={{ borderColor: 'var(--border-soft)' }}
        />
        <button
          type="submit"
          disabled={loading || !query.trim() || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          className="rounded-md bg-accent-purple px-4 py-2 text-xs font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
        {loading && onBreak && (
          <button
            type="button"
            onClick={onBreak}
            disabled={breaking}
            className="rounded-md border px-4 py-2 text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}
          >
            {breaking ? 'Breaking...' : 'Break'}
          </button>
        )}
      </form>

      {searchInput && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] text-text-secondary">
          <span className="text-text-muted">Search query:</span>
          <span
            className="rounded px-1.5 py-0.5 font-medium"
            style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}
          >{searchInput}</span>
          {resolvedEn && (
            <span className="rounded px-1.5 py-0.5" style={{ background: 'var(--color-bg-panel)' }}>EN · {resolvedEn}</span>
          )}
          {resolvedKo && (
            <span className="rounded px-1.5 py-0.5" style={{ background: 'var(--color-bg-panel)' }}>KO · {resolvedKo}</span>
          )}
        </div>
      )}
    </div>
  )
}
